import React from 'react'

import { Button, IconButton as MuiIconButton, Fab, Tooltip, CircularProgress, FabProps, ButtonProps, Box, SxProps, Theme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import MenuIcon from '@mui/icons-material/Menu'
import ExitToApp from '@mui/icons-material/ExitToApp'
import Search from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { J2Delete } from '@e3dc-react/icons'
import SaveIcon from '@mui/icons-material/Save'

import { LinkRouter } from '../Links'
import { Typography } from '../Typography'

interface StyleProps {
  hasUnderline?: boolean
  linkColor?: string
  hasLink?: boolean
  fullWidth?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  margin: {
    marginTop: theme.spacing(1),
  },
  extendedIcon: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  linkButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: props.hasLink ? 'pointer' : 'default',
    textDecoration: props.hasUnderline ? 'underline' : 'none',
    display: 'inline',
    color: props.hasLink ? props.linkColor ?? theme.palette.action.active : undefined,
    margin: 0,
    padding: 0,
    '&:hover,&:focus': {
      textDecoration: 'none',
    },
    '&:disabled': {
      cursor: 'default',
      color: theme.palette.action.disabled,
    },
    textAlign: 'left',
  },
  wrapper: {
    position: 'relative',
    width: props.fullWidth ? '100%' : 'auto',
  },
  buttonProgress: {
    color: theme.palette.primary.main,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  iconButton: {
    borderRadius: 12,
    padding: 6,
  },
  iconButtonBackground: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    transition: 'all 0.2s ease-in-out',

    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transition: 'all 0.2s ease-in-out',
    },
    '&:disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
}))

export interface StandardButtonProps {
  label?: string
  buttonProps?: Partial<FabProps> | Partial<ButtonProps>
  tooltip?: string
  className?: string
  disabled?: boolean
  onClick?: () => void | Promise<void>
  size?: 'small' | 'medium' | 'large'
}

export const AddButton: React.FC<StandardButtonProps> = ({ onClick, label, buttonProps, tooltip, className, disabled, size }) => (
  <ActionButton
    onClick={onClick}
    label={label}
    icon={props => <AddIcon {...props} />}
    buttonProps={buttonProps}
    tooltip={tooltip}
    className={className}
    disabled={disabled}
    size={size}
  />
)

export const EditButton: React.FC<StandardButtonProps> = ({ onClick, label, buttonProps, tooltip, className }) => (
  <ActionButton
    onClick={onClick}
    label={label}
    icon={props => <EditIcon {...props} />}
    buttonProps={buttonProps}
    tooltip={tooltip}
    className={className}
  />
)

export const SaveButton: React.FC<StandardButtonProps> = ({ onClick, label, buttonProps, tooltip, className, disabled = false }) => (
  <ActionButton
    onClick={onClick}
    label={label}
    icon={props => <SaveIcon {...props} />}
    buttonProps={{ ...buttonProps, color: 'secondary', variant: 'contained' }}
    tooltip={tooltip}
    className={className}
    disabled={disabled}
  />
)

export const RemoveButton: React.FC<StandardButtonProps> = ({ onClick, label, buttonProps, tooltip }) => (
  <ActionButton onClick={onClick} label={label} icon={props => <DeleteIcon {...props} />} buttonProps={buttonProps} tooltip={tooltip} />
)

export interface ActionButtonProps extends StandardButtonProps {
  icon: (props?: { className: string }) => React.ReactElement
}

export const ActionButton: React.FC<ActionButtonProps> = ({ onClick, label, icon, buttonProps, tooltip, className, disabled, size = 'small' }) => {
  const { classes, cx } = useStyles({})
  let button
  if (!label) {
    button = (
      <Fab
        color="secondary"
        size={size}
        className={cx(classes.margin, className)}
        onClick={onClick}
        disabled={disabled}
        {...(buttonProps as FabProps)}
      >
        {icon({ className: classes.extendedIcon })}
      </Fab>
    )
  } else {
    button = (
      <Button
        variant="outlined"
        color={'secondary'}
        startIcon={icon()}
        onClick={onClick}
        className={className}
        disabled={disabled}
        {...(buttonProps as ButtonProps)}
      >
        {label}
      </Button>
    )
  }

  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        <span>{button}</span>
      </Tooltip>
    )
  }

  return button
}

export interface LinkButtonProps {
  link?: string
  hasUnderline?: boolean
  linkColor?: string
  content: React.ReactNode
  onClick?: () => void
  linkClassName?: string
  clickable?: boolean
  dataQa?: string
  href?: string
  type?: 'button' | 'reset' | 'submit'
}

/**
 * Provides either a native link element or a react-router link, wrapped into a native link object.
 *
 * The first is used to provide link experience to resources outside the application.
 * The second is used for application internal navigation elements, that shall look like a link but behave as a button.
 *
 * Example:
 *
 * This will provide a native html link
 * ```
 * <LinkButton link="http://somwhere-else.com/agb">Link to partner AGB</<LinkButton>
 * ```
 *
 * This will provide a button styled a link, which ensures the behaviour of the internal router
 * ```
 * <LinkButton link="/deep/route/123">Link a internal route</<LinkButton>
 * ```
 *
 * @param link
 * @param hasUnderline
 * @param linkColor
 * @param content
 * @param onClick
 * @param clickable
 * @param linkClassName
 * @param dataQa
 * @constructor
 */
export const LinkButton: React.FC<LinkButtonProps> = ({
  type = 'button',
  link,
  hasUnderline = true,
  linkColor,
  content,
  onClick,
  clickable = true,
  linkClassName,
  dataQa,
  ...rest
}) => {
  const { classes, cx } = useStyles({ hasUnderline, linkColor, hasLink: !!link || clickable })

  if (clickable && link)
    return (
      <LinkRouter className={cx(classes.linkButton, linkClassName)} to={link} disabled={!clickable} onClick={onClick} dataQa={dataQa} {...rest}>
        {content}
      </LinkRouter>
    )
  return (
    <button
      type={type}
      disabled={!clickable}
      className={cx(classes.linkButton, linkClassName)}
      onClick={() => onClick?.()}
      data-qa={dataQa}
      {...rest}
    >
      {content}
    </button>
  )
}

export type IconButtonType = 'menu' | 'logout' | 'search' | 'edit' | 'remove'
export type IconButtonSize = 'small' | 'medium'

export interface GenericIconButtonProps {
  onClick: () => void
  size?: IconButtonSize
  color?: 'primary' | 'secondary' | 'inherit'
}

export interface TypedIconButtonProps extends GenericIconButtonProps {
  type?: IconButtonType
  icon?: React.ReactNode
  withBackground?: boolean
  disabled?: boolean
  dataQa?: string
}

export const IconButton: React.FC<TypedIconButtonProps> = ({
  type,
  onClick,
  color,
  size = 'medium',
  icon,
  withBackground = false,
  disabled = false,
  dataQa,
}) => {
  const { classes, cx } = useStyles({})
  let iconToUse

  if (type) {
    switch (type.toLowerCase()) {
      case 'menu':
        iconToUse = <MenuIcon />
        break
      case 'logout':
        iconToUse = <ExitToApp />
        break
      case 'search':
        iconToUse = <Search />
        break
      case 'edit':
        iconToUse = <EditIcon />
        break
      case 'remove':
        iconToUse = <J2Delete />
        break
      default:
    }
  }

  return (
    <MuiIconButton
      className={cx(classes.iconButton, { [classes.iconButtonBackground]: withBackground })}
      color={color ?? 'inherit'}
      onClick={onClick}
      size={size}
      disabled={disabled}
      data-qa={dataQa}
    >
      {icon ? icon : iconToUse}
    </MuiIconButton>
  )
}

export const MenuIconButton: React.FC<GenericIconButtonProps> = ({ onClick, size, color }) => {
  return <IconButton type="menu" onClick={onClick} size={size} color={color} />
}

export const LogoutIconButton: React.FC<GenericIconButtonProps> = ({ onClick, size, color }) => {
  return <IconButton type="logout" onClick={onClick} size={size} color={color} />
}

export const SearchIconButton: React.FC<GenericIconButtonProps> = ({ onClick, size, color }) => {
  return <IconButton type="search" onClick={onClick} size={size} color={color} />
}
export const EditIconButton: React.FC<GenericIconButtonProps> = ({ onClick, size, color }) => {
  return <IconButton type="edit" onClick={onClick} size={size} color={color} />
}

export const RemoveIconButton: React.FC<GenericIconButtonProps> = ({ onClick, size = 'medium', color }) => {
  return <IconButton type="remove" onClick={onClick} size={size} color={color} />
}

export interface ButtonWithLoadingProps extends StandardButtonProps {
  isLoading?: boolean
  fullWidth?: boolean
  icon?: React.ReactElement
  variant?: ButtonProps['variant']
  color?: ButtonProps['color']
  dataQa?: string
  type?: 'submit'
  sx?: SxProps<Theme>
}

export const ButtonWithLoading: React.FC<ButtonWithLoadingProps> = ({
  label,
  onClick,
  fullWidth,
  className,
  icon,
  color,
  isLoading = false,
  disabled = false,
  dataQa,
  variant = 'contained',
  buttonProps,
  type,
  sx,
}) => {
  const { classes } = useStyles({ fullWidth })

  return (
    <Box className={classes.wrapper}>
      <Button
        {...buttonProps}
        sx={sx}
        variant={variant ?? 'contained'}
        color={color}
        disabled={disabled || isLoading}
        onClick={onClick}
        fullWidth={fullWidth}
        className={className}
        startIcon={icon}
        data-qa={dataQa}
        type={type}
      >
        <Typography variant={'button'}>{label}</Typography>
      </Button>
      {isLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
    </Box>
  )
}
